import React from "react";
import { useGame } from "@/contexts/GameContext";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trophy, Lock, Swords, BookOpen, Map, Sparkles, Star, Heart, Shield, Package, Users, Zap, Crown, Flame, Eye, Target } from "lucide-react";
import { t } from "@/utils/i18n";
import useTabMusic from "@/hooks/useTabMusic";

const ACHIEVEMENT_ICONS = {
  book: BookOpen, sword: Swords, map: Map, sparkle: Sparkles,
  star: Star, heart: Heart, shield: Shield, package: Package,
  users: Users, zap: Zap, crown: Crown, flame: Flame, eye: Eye, target: Target, trophy: Trophy
};

const ACHIEVEMENT_DEFS = [
  { id: "first_steps", icon: "book", color: "#cfb53b", tier: "bronze" },
  { id: "bookworm", icon: "book", color: "#cfb53b", tier: "silver" },
  { id: "epic_tale", icon: "book", color: "#cfb53b", tier: "gold" },
  { id: "first_blood", icon: "sword", color: "#dc2626", tier: "bronze" },
  { id: "warrior_ach", icon: "sword", color: "#dc2626", tier: "silver" },
  { id: "champion", icon: "sword", color: "#dc2626", tier: "gold" },
  { id: "combo_master", icon: "zap", color: "#a855f7", tier: "silver" },
  { id: "combo_king", icon: "zap", color: "#a855f7", tier: "gold" },
  { id: "collector", icon: "package", color: "#10b981", tier: "bronze" },
  { id: "hoarder", icon: "package", color: "#10b981", tier: "silver" },
  { id: "blacksmith", icon: "flame", color: "#f97316", tier: "silver" },
  { id: "legendary_smith", icon: "flame", color: "#f97316", tier: "gold" },
  { id: "explorer", icon: "map", color: "#3b82f6", tier: "bronze" },
  { id: "cartographer", icon: "map", color: "#3b82f6", tier: "silver" },
  { id: "level_5", icon: "star", color: "#cfb53b", tier: "silver" },
  { id: "level_10", icon: "crown", color: "#cfb53b", tier: "gold" },
  { id: "survivor", icon: "heart", color: "#dc2626", tier: "silver" },
  { id: "art_collector", icon: "eye", color: "#a855f7", tier: "bronze" },
  { id: "full_set", icon: "shield", color: "#10b981", tier: "bronze" },
  { id: "guardian", icon: "users", color: "#064e3b", tier: "silver" },
  { id: "boss_slayer", icon: "crown", color: "#cfb53b", tier: "bronze" },
  { id: "boss_hunter", icon: "crown", color: "#cfb53b", tier: "silver" },
  { id: "boss_legend", icon: "crown", color: "#cfb53b", tier: "gold" },
  { id: "rare_collector", icon: "sparkle", color: "#a855f7", tier: "silver" },
];

const ACHIEVEMENT_NAMES = {
  en: {
    first_steps: ["First Steps", "Complete your first story page"],
    bookworm: ["Bookworm", "Read 10 story pages"],
    epic_tale: ["Epic Tale", "Read 25 story pages"],
    first_blood: ["First Blood", "Win your first battle"],
    warrior_ach: ["Warrior", "Win 5 battles"],
    champion: ["Champion", "Win 10 battles"],
    combo_master: ["Combo Master", "Trigger 5 combos total"],
    combo_king: ["Combo King", "Trigger all 6 combo types"],
    collector: ["Collector", "Collect 10 cards total"],
    hoarder: ["Hoarder", "Collect 25 cards total"],
    blacksmith: ["Blacksmith", "Upgrade a card to level 3"],
    legendary_smith: ["Legendary Smith", "Upgrade a card to level 5"],
    explorer: ["Explorer", "Visit 5 locations"],
    cartographer: ["Cartographer", "Visit 10 locations"],
    level_5: ["Veteran", "Reach level 5"],
    level_10: ["Legend", "Reach level 10"],
    survivor: ["Survivor", "Win a battle with less than 5 HP"],
    art_collector: ["Art Collector", "Generate artwork for 5 cards"],
    full_set: ["Full Set", "Have at least 1 ally, 1 object, and 1 ability"],
    guardian: ["Guardian", "Have 3 allies simultaneously"],
    boss_slayer: ["Boss Slayer", "Defeat your first boss"],
    boss_hunter: ["Boss Hunter", "Defeat 3 bosses"],
    boss_legend: ["Boss Legend", "Defeat 5 bosses"],
    rare_collector: ["Rare Collector", "Collect 3 rare or epic cards"],
  },
  it: {
    first_steps: ["Primi Passi", "Completa la tua prima pagina"],
    bookworm: ["Topo di Biblioteca", "Leggi 10 pagine della storia"],
    epic_tale: ["Racconto Epico", "Leggi 25 pagine della storia"],
    first_blood: ["Primo Sangue", "Vinci la tua prima battaglia"],
    warrior_ach: ["Guerriero", "Vinci 5 battaglie"],
    champion: ["Campione", "Vinci 10 battaglie"],
    combo_master: ["Maestro Combo", "Attiva 5 combo totali"],
    combo_king: ["Re delle Combo", "Attiva tutti i 6 tipi di combo"],
    collector: ["Collezionista", "Raccogli 10 carte totali"],
    hoarder: ["Accumulatore", "Raccogli 25 carte totali"],
    blacksmith: ["Fabbro", "Potenzia una carta al livello 3"],
    legendary_smith: ["Fabbro Leggendario", "Potenzia una carta al livello 5"],
    explorer: ["Esploratore", "Visita 5 luoghi"],
    cartographer: ["Cartografo", "Visita 10 luoghi"],
    level_5: ["Veterano", "Raggiungi il livello 5"],
    level_10: ["Leggenda", "Raggiungi il livello 10"],
    survivor: ["Sopravvissuto", "Vinci una battaglia con meno di 5 PV"],
    art_collector: ["Collezionista d'Arte", "Genera arte per 5 carte"],
    full_set: ["Set Completo", "Possiedi almeno 1 alleato, 1 oggetto e 1 abilita"],
    guardian: ["Guardiano", "Possiedi 3 alleati contemporaneamente"],
    boss_slayer: ["Ammazza Boss", "Sconfiggi il tuo primo boss"],
    boss_hunter: ["Cacciatore di Boss", "Sconfiggi 3 boss"],
    boss_legend: ["Leggenda dei Boss", "Sconfiggi 5 boss"],
    rare_collector: ["Collezionista Raro", "Colleziona 3 carte rare o epiche"],
  },
  es: {
    first_steps: ["Primeros Pasos", "Completa tu primera pagina"],
    first_blood: ["Primera Sangre", "Gana tu primera batalla"],
    victory: ["Victoria", "Gana una batalla"], combo_master: ["Maestro Combo", "Activa 5 combos"],
    collector: ["Coleccionista", "Colecciona 10 cartas"], explorer: ["Explorador", "Visita 5 lugares"],
  },
};

const TIER_STYLES = {
  bronze: "from-[#cd7f32]/20 to-[#cd7f32]/5 border-[#cd7f32]/30",
  silver: "from-[#c0c0c0]/20 to-[#c0c0c0]/5 border-[#c0c0c0]/30",
  gold: "from-[#cfb53b]/20 to-[#cfb53b]/5 border-[#cfb53b]/30",
};

export default function Achievements() {
  const { game } = useGame();
  useTabMusic('trophies');  // Iter 104 — per-section music
  if (!game) return null;

  const lang = game.language || "en";
  const unlockedAchs = game.achievements || [];
  const unlockedIds = new Set(unlockedAchs.map(a => a.id));
  const totalUnlocked = unlockedIds.size;

  return (
    <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="achievements-view">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="font-heading text-base sm:text-xl text-[#cfb53b] flex items-center gap-2">
            <Trophy size={18} /> {t(lang, 'achievements_title')}
          </h2>
          <span className="text-xs sm:text-sm text-[#94a3b8] font-heading">
            {totalUnlocked}/{ACHIEVEMENT_DEFS.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-4 sm:mb-6">
          <div className="h-2 bg-[#1a1a1d] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#cfb53b] to-[#d4af37] transition-all duration-500"
              style={{ width: `${(totalUnlocked / ACHIEVEMENT_DEFS.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 stagger-children">
          {ACHIEVEMENT_DEFS.map(ach => {
            const isUnlocked = unlockedIds.has(ach.id);
            const Icon = ACHIEVEMENT_ICONS[ach.icon] || Trophy;
            const names = ACHIEVEMENT_NAMES[lang]?.[ach.id] || ACHIEVEMENT_NAMES.en?.[ach.id] || [ach.id, ""];
            const [achName, achDesc] = names;
            const unlockData = unlockedAchs.find(a => a.id === ach.id);

            return (
              <div
                key={ach.id}
                data-testid={`achievement-${ach.id}`}
                className={`rounded-lg border p-3 sm:p-4 transition-all animate-fadeInUp ${
                  isUnlocked
                    ? `bg-gradient-to-r ${TIER_STYLES[ach.tier]}`
                    : 'bg-[#1a1a1d]/40 border-[#2d3748] opacity-60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    isUnlocked ? 'bg-[#0a0a0c]/40' : 'bg-[#0a0a0c]/60'
                  }`}>
                    {isUnlocked ? (
                      <Icon size={20} style={{ color: ach.color }} />
                    ) : (
                      <Lock size={16} className="text-[#64748b]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-heading text-sm truncate ${isUnlocked ? 'text-[#e2e8f0]' : 'text-[#64748b]'}`}>
                      {achName}
                    </h4>
                    <p className="text-xs text-[#94a3b8] truncate">{achDesc}</p>
                    {isUnlocked && unlockData?.unlocked_at && (
                      <p className="text-[10px] text-[#64748b] mt-0.5">
                        {new Date(unlockData.unlocked_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  {isUnlocked && (
                    <Trophy size={14} style={{ color: ach.tier === 'gold' ? '#cfb53b' : ach.tier === 'silver' ? '#c0c0c0' : '#cd7f32' }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
